/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {
  makeStyles,
  Slider,
  SliderProps
} from '@material-ui/core';
import classnames from 'classnames';
import {dlsColor} from '@ion/dls/src/theme/theme';

export interface SliderTypes extends SliderProps {
  onSlide?: (event: any, value: number | number[]) => void,
  hideThumb?: boolean;
  input?: boolean;
  progress?: boolean;
  // stretches the slider to its container's width (less 12px, so the
  // thumb isn't clipped at the right edge) instead of the default fixed
  // 206px - e.g. the Map Layers panel's opacity sliders ...
  fullWidth?: boolean;
}

const useStyles = makeStyles(() => ({
  root : {
    width     : 206,
    height    : 4,
    padding   : '13px 0',
    overflow  : 'visible',
    position  : 'relative',
    boxSizing : 'content-box'
  },
  rootFullWidth : {
    width : 'calc(100% - 12px)'
  },
  thumb : {
    width                        : 16,
    height                       : 16,
    boxShadow                    : '0px 1px 3px 0px #00000033, 0px 2px 2px 0px #0000001F, 0px 0px 2px 0px #00000024',
    marginTop                    : -6,
    borderRadius                 : 10,
    backgroundColor              : dlsColor('darkTeal'),
    '&:focus, &:hover, &$active' : {
      boxShadow : '0px 1px 3px 0px #00000033, 0px 2px 2px 0px #0000001F, 0px 0px 2px 0px #00000024'
    },
    '&$disabled' : {
      backgroundColor : dlsColor('grey4')
    }
  },
  track : {
    height          : 4,
    borderRadius    : 100,
    backgroundColor : dlsColor('darkTeal'),
    '&$disabled'    : {
      backgroundColor : dlsColor('grey4')
    }
  },
  rail : {
    height          : 4,
    opacity         : 1,
    borderRadius    : 100,
    backgroundColor : dlsColor('paleBlue'),
    '&$disabled'    : {
      opacity         : 0.3,
      backgroundColor : dlsColor('grey4')
    }
  },
  valueLabel : {
    top        : '-40px !important' as any,
    left       : 'auto !important' as any,
    right      : '0px !important' as any,
    opacity    : '1 !important' as any,
    position   : 'absolute !important' as any,
    transform  : 'none !important' as any,
    '& > span' : {
      gap             : '4px',
      color           : dlsColor('darkTeal'),
      width           : '72px',
      border          : `1px solid ${dlsColor('arcticBlue')}`,
      height          : '28px',
      display         : 'flex',
      padding         : '0 8px',
      transform       : 'none !important',
      alignItems      : 'center',
      borderRadius    : '8px',
      justifyContent  : 'center',
      backgroundColor : dlsColor('aquamarine')
    },
    '& span > span' : {
      fontSize      : '14px',
      transform     : 'none',
      fontWeight    : 500,
      letterSpacing : '1px'
    }
  },
  active   : {},
  disabled : {}
}));

/**
 * A styled Material UI Slider featuring a custom track, rail, and thumb design.
 * Supports a "wInput" mode which displays a fixed-position numeric badge.
 */
export const SliderWrapper = ({
  onSlide,
  hideThumb = false,
  input = false,
  progress = false,
  fullWidth = false,
  ...props
}: SliderTypes) => {
  const classes = useStyles();

  return (
    <Slider classes={{
      rail     : classes.rail,
      root     : classnames(classes.root, fullWidth && classes.rootFullWidth),
      thumb    : classes.thumb,
      track    : classes.track,
      active   : classes.active,
      disabled : classes.disabled
    }}
    onChange={onSlide}
    {...props} />
  );
};

export default SliderWrapper;
